/**
 * Serialize ActionDefs into token-efficient LLM context.
 *
 * Two detail levels:
 * - compact: for command tier (gpt-3.5-turbo 16k) — ~150 tokens/action
 * - full: for workflow tier (o3-mini 128k) — ~300 tokens/action
 *
 * @module gibbs/text-to-action/serializeRegistry
 */

import { getCompatibilityRules } from '../registry/index';

/**
 * Serialize an ActionDef to a compact JSON object for LLM context.
 * Omits empty arrays and null execution references to save tokens.
 *
 * @param {import('../registry/schema.js').ActionDef} action
 * @param {'compact' | 'full'} [level='compact']
 * @returns {Object}
 */
export function serializeAction(action, level = 'compact') {
    const compact = {
        id: action.id,
        name: action.name,
        desc: action.description,
        inputs: action.inputs.map((p) => ({
            name: p.name,
            type: p.type,
            ...(p.required === false ? { req: false } : {}),
        })),
        outputs: action.outputs.map((p) => ({
            name: p.name,
            type: p.type,
        })),
        runtime: action.runtime,
    };

    // Only include params that have options or defaults (most useful for LLM)
    const relevantParams = action.parameters.filter(
        (p) => p.options || p.defaultValue !== undefined
    );
    if (relevantParams.length > 0) {
        compact.params = relevantParams.map((p) => {
            const param = { name: p.name, type: p.type };
            if (p.options) param.options = p.options;
            if (p.defaultValue !== undefined) param.default = p.defaultValue;
            if (p.required === false) param.req = false;
            return param;
        });
    }

    // Include preconditions as string array
    if (action.preconditions.length > 0) {
        compact.pre = action.preconditions.map((p) => p.check);
    }

    if (level === 'full') {
        // Full includes all parameters, postconditions, dependencies, category
        compact.category = action.category;
        compact.status = action.status;
        compact.params = action.parameters.map((p) => {
            const param = { name: p.name, type: p.type, desc: p.description };
            if (p.options) param.options = p.options;
            if (p.defaultValue !== undefined) param.default = p.defaultValue;
            if (p.required === false) param.req = false;
            if (p.constraints) param.constraints = p.constraints;
            return param;
        });
        if (action.postconditions.length > 0) {
            compact.post = action.postconditions.map((p) => p.guarantee);
        }
        if (action.dependsOn.length > 0) {
            compact.dependsOn = action.dependsOn;
        }
    }

    return compact;
}

/**
 * Serialize a list of ActionDefs into a complete registry context block.
 *
 * @param {import('../registry/schema.js').ActionDef[]} actions
 * @param {'compact' | 'full'} [level='compact']
 * @returns {{ actions: Object[], typeRules?: Object }}
 */
export function serializeRegistryContext(actions, level = 'compact') {
    const context = {
        actions: actions.map((a) => serializeAction(a, level)),
    };

    // For full level, include type compatibility rules
    if (level === 'full') {
        context.typeRules = getCompatibilityRules();
    }

    return context;
}

/**
 * Estimate token count for a serialized context.
 * Rough estimate: ~4 characters per token for JSON.
 *
 * @param {Object} context - Serialized context from serializeRegistryContext
 * @returns {number} Estimated token count
 */
export function estimateTokens(context) {
    const json = JSON.stringify(context);
    return Math.ceil(json.length / 4);
}
